import { Injectable } from '@angular/core';
import { ELoanServiceCode } from '@common/models';
import { AbstractControl, ControlContainer, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IContextualMenu } from '../../common/models/contextual-menu.model';
import { HttpRequestPayload, IHttpErrorPayload, IHttpSuccessPayload } from '../../core/models/http.model';
import { HttpProviderService } from '../../core/services/http-provider/http-provider.service';
import { LoanClosureSimulation } from '../../models/loan-closure-simulation.mode';
import { ILoanInfo, LoanDetails } from '../../models/loan-details.model';
import { ILoanDisbursementSchedule } from '../../models/loan-disbursement-schedule.model';
import { LoanRepaymentSchedule } from '../../models/loan-repayment-schedule.model';
import { LoanTransactionDetail } from '../../models/loan-transaction-detail.model';
import { ProvidehttpService } from '../../providehttp.service';
import { CustomerService } from '../customer.service';

@Injectable({
  providedIn: 'root'
})
export class LoanService {
  private _loanInfo:Map<string, ILoanInfo> = new Map<string, ILoanInfo>();

  private selectedLoanAccount:LoanDetails;
  private loanDisbursalDetails:Map<string, ILoanDisbursementSchedule[]> = new Map<string, ILoanDisbursementSchedule[]>();

  contextualMenuList:IContextualMenu[] = [
    {
      id: 'B001',
      icon: 'loan-repayment-request',
      name: 'Pay loan <em>installment</em>',
      title: 'Pay Loan Installment',
      path: ['shell', 'loans', 'loan-repayment-request'],
      serviceCode : ELoanServiceCode.LOAN_REPAYMENT_REQUEST
    },
    {
      id: 'B002',
      icon: 'loan-partial-pre-payment',
      name: 'Partial <em>payment</em>',
      title: 'Loan Partial Payment',
      path: ['shell', 'loans', 'loan-partial-pre-payment'],
      serviceCode : ELoanServiceCode.LOAN_PARTIAL_PAYMENT
    },
    {
      id: 'B003',
      icon: 'loan-pre-closure',
      name: 'Pre <em>closure</em>',
      title: 'Loan Pre Closure',
      path: ['shell', 'loans', 'loan-closure'],
      serviceCode : ELoanServiceCode.LOAN_PRE_CLOSURE
    },
    {
      id: "B004",
      icon: "loan-reschedule-request",
      name: "Reschedule <em>request</em>",
      title: "Loan Reschedule Request",
      path: ["shell", "loans", "loan-reschedule-request"],
      serviceCode : ELoanServiceCode.LOAN_RESCHEDULE_REQUEST
    },
    {
      id: "B005",
      icon: "loan-repayment-schedule-simulation",
      name: "Reschedule <em>simulation</em>",
      title: "Loan Reschedule Simulation",
      path: ["shell", "loans", "repayment-schedule-simulation"],
      serviceCode : ELoanServiceCode.LOAN_REPAYMENT_SCHEDULE_SIMULATION
    },
    {
      id: "B006",
      icon: "loan-closure-simulation",
      name: "Closure <em>simulation</em>",
      title: "Loan Closure Simulation",
      path: ["shell", "loans", "loan-closure-simulation"],
      serviceCode : ELoanServiceCode.LOAN_CLOSURE_SIMULATION
    },
    {
      id: "B007",
      icon: "loan-disbursal-schedule",
      name: "Disbursal <em>Schedule</em>",
      title: "Loan Disbursal Schedule",
      path: ["shell", "loans", "disbursement-schedule"],
      serviceCode : ELoanServiceCode.LOAN_DISBURSAL_SCHEDULE
    },
    
  ]
  
  constructor(
    private _httpProvider: HttpProviderService,
    private _customerService:CustomerService
  ) { }

  getMenuList(): IContextualMenu[] {
    return this.contextualMenuList;
  }

  getSelectedMenuData(menuId:string){
    const selectedMenuItem = this.contextualMenuList.find(item => item.id == menuId);
    return selectedMenuItem;
  }

  /**
   *
   * @param accountNumber
   * @returns LoanDetails
   */
  fetchLoanDetails(accountNumber: string): Observable<LoanDetails> {
    let loanDetails: LoanDetails;
    const httpRequest : HttpRequestPayload = {
      resource : "/loan/" + accountNumber,
      method : "GET"
    }
    
    return this._httpProvider.invokeApi(httpRequest).pipe(
      map((res : IHttpSuccessPayload<any>) => {
        loanDetails = res.body['loan'];
        return loanDetails;
      }),
      catchError((err : IHttpErrorPayload) => { 
        return throwError(err)
    }));
  }

  /**
   * 
   * @param accountNumber 
   * @returns LoanRepaymentShedule[]
   */
  fetchLoanRepaymentSchedule(accountNumber: string): Observable<LoanRepaymentSchedule[]> {
    const httpRequest : HttpRequestPayload = {
      resource : "/loan/repayment/schedule/" + accountNumber,
      method : "GET"
    }
    
    return this._httpProvider.invokeApi(httpRequest).pipe(
      map((res : IHttpSuccessPayload<any>) => {
        return res.body['loan'];
      }),
      catchError((err : IHttpErrorPayload) => { 
        return throwError(err);
    }));
  }

  fetchLoanDisbursementSchedule(accountNumber: string): Observable<ILoanDisbursementSchedule[]> {
    const httpRequest : HttpRequestPayload = {
      resource : "/loan/disbursal/schedule/" + accountNumber,
      method : "GET"
    }
    
    return this._httpProvider.invokeApi(httpRequest).pipe(
      map((res : IHttpSuccessPayload<any>) => {
        this.loanDisbursalDetails[accountNumber] = res.body['loan'];
        return this.loanDisbursalDetails[accountNumber];
      }),
      catchError((err : IHttpErrorPayload) => { 
        return throwError(err);
    }));
  }

  fetchLoanClosureSimulation(accountNumber: string): Observable<LoanClosureSimulation[]> {

    const httpRequest : HttpRequestPayload = {
      resource : "/loan/closure/simulation/" + accountNumber,
      method : "GET",
      queryParams: [
        {
          key: 'closureDate',
          value: '2022-05-30'
        }
      ]
    }
    
    return this._httpProvider.invokeApi(httpRequest).pipe(
      map((res : IHttpSuccessPayload<any>) => {
        return res.body['loan'];
      }),
      catchError((err : IHttpErrorPayload) => { 
        return throwError(err);
    }));

  }

  /**
   * 
   * @description To fetch loan statement based on a loan accountNumber
   * @param accountNumber 
   */
  fetchLoanStatement(accountNumber: string): Observable<LoanTransactionDetail[]> {

    const httpRequest : HttpRequestPayload = {
      resource : "/loan/statement/" + accountNumber,
      method : "GET",
      queryParams: [
        {
          key: 'startDate',
          value: '2020-01-01'
        },
        {
          key: 'endDate',
          value: '2022-05-30'
        }
      ]
    }
    
    return this._httpProvider.invokeApi(httpRequest).pipe(
      map((res : IHttpSuccessPayload<any>) => {
        return (res.body && res.body['loan']) ? res.body['loan'] : [];
      }),
      catchError((err : IHttpErrorPayload) => { 
        return throwError(err);
    }));
  }

  /**
   * Instead of me(getLoanDetails) use getDetails
   * @param loanAccountNumber 
   * @returns 
   */
  getLoanDetails(loanAccountNumber:string):LoanDetails{
    const loanDetails:LoanDetails = this._customerService.getLoanDetails().find(item => item.accountNumber == loanAccountNumber);
    return loanDetails || null;
  }

  /**
   * 
   * @param loanAccountNumber 
   * @returns 
   */
   getDetails(loanAccountNumber:string):LoanDetails{
    const loanDetails:LoanDetails = this._customerService.getLoanDetails().find(item => item.accountNumber == loanAccountNumber);
    return loanDetails || null;
  }
  
  getLoanInfo(loanAccountNumber:string):Observable<ILoanInfo>{
    if(this._loanInfo.has(loanAccountNumber)){
      return of(this._loanInfo.get(loanAccountNumber));
    }else {
      return this.fetchLoanInfo(loanAccountNumber).pipe(
        map(
          (res) => {
            return this._loanInfo.get(loanAccountNumber)
          }
        )
      );
    }
  }

  /**
   * 
   * @param loanAccountNumber 
   * @returns depositInfo
   */
   getLoanDisbusalData(loanAccountNumber:string, disbursalId:number):ILoanDisbursementSchedule{
    if(this.loanDisbursalDetails.has(loanAccountNumber)){
      const loanDisbursalData:ILoanDisbursementSchedule = this.loanDisbursalDetails.get(loanAccountNumber).find( item => item.disbursalId == disbursalId );
      return loanDisbursalData || null;
    }
    
  }
   fetchLoanInfo(loanAccountNumber:string):Observable<ILoanInfo>{
    const httpRequest : HttpRequestPayload = {
      resource : "/loan/" + loanAccountNumber,
      method : "GET"
    }
    
    return this._httpProvider.invokeApi(httpRequest).pipe(
      map((res : IHttpSuccessPayload<any>) => {
        this._loanInfo.set(loanAccountNumber, res.body['loan']);
          return this._loanInfo.get(loanAccountNumber);
      }));
  }
  
}
